import React, { useEffect, useRef, useState } from 'react';
import { useNavigate }    from 'react-router-dom';
import { getFirestore, collection, getDocs } from 'firebase/firestore';
import { useSuperAdmin }  from '../contexts/SuperAdminContext.js';
import './styles/SuperAdmin.css';

const SuperAdminTopBar = ({ openedWorkspace = null, onBackToList = null }) => {
  const navigate = useNavigate();
  const { isSuperAdmin, isImpersonating, impersonation, stopImpersonation, impersonateOrg } = useSuperAdmin();

  const [users,        setUsers]        = useState([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [loading,      setLoading]      = useState(false);
  const dropdownRef = useRef(null);

  // ── close on outside click ───────────────────────────────────────────────────
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // ── load users when dropdown opens ──────────────────────────────────────────
  useEffect(() => {
    if (!dropdownOpen || !impersonation?.orgId) return;
    if (users.length > 0) return; // already loaded

    const load = async () => {
      setLoading(true);
      try {
        const db   = getFirestore();
        const snap = await getDocs(
          collection(db, `Organisations/${impersonation.orgId}/Users`)
        );
        const list = snap.docs.map((d) => {
          const data = d.data() || {};
          return {
            uid:         data.uid       || d.id,
            firstName:   data.firstName || '',
            lastName:    data.lastName  || '',
            email:       data.email     || '',
            role:        data.role      || '',
            displayName: `${data.firstName || ''} ${data.lastName || ''}`.trim() || data.email || d.id
          };
        }).sort((a, b) => a.displayName.localeCompare(b.displayName));
        setUsers(list);
      } catch (e) {
        console.error('TopBar: failed to load users', e);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [dropdownOpen, impersonation?.orgId]);

  const handleSwitchUser = async (user) => {
    setDropdownOpen(false);
    setUsers([]); // clear so list reloads fresh for new user if org ever changes

    try {
      await impersonateOrg(impersonation.orgId, impersonation.role || 'orgAdmin', {
        uid:       user.uid,
        email:     user.email,
        firstName: user.firstName,
        lastName:  user.lastName,
        orgName:   impersonation.orgName || impersonation.orgId
      });
      // ── no reload needed — context state update re-renders everything ────
    } catch (e) {
      console.error('Switch user failed:', e);
    }
  };

  const handleExitAndReturn = async () => {
    try { await stopImpersonation(); }
    finally { navigate('/admin'); }
  };

  if (!isSuperAdmin || !isImpersonating) return null;

  const userLabel =
    `${impersonation?.firstName || ''} ${impersonation?.lastName || ''}`.trim() ||
    impersonation?.email || impersonation?.uid || 'Unknown user';

  const orgLabel = impersonation?.orgName || impersonation?.orgId || 'Unknown org';

  return (
    <div className="sa-topbar">

      {/* Left */}
      <div className="sa-topbar-left">
        <span className="material-icons-sharp sa-topbar-icon">security</span>

        {openedWorkspace ? (
          <>
            <button className="sa-topbar-back" onClick={onBackToList}>
              <span className="material-icons-sharp">arrow_back</span>
              Workspaces
            </button>
            <span className="sa-topbar-sep">/</span>
            <span className="sa-topbar-workspace">{openedWorkspace.name}</span>
          </>
        ) : (
          <span className="sa-topbar-label">
            Viewing as <strong>{userLabel}</strong>
            <span className="sa-topbar-role">{impersonation?.role || 'orgAdmin'}</span>
            <span className="sa-topbar-at">@</span>
            <strong>{orgLabel}</strong>
          </span>
        )}
      </div>

      {/* Right */}
      <div className="sa-topbar-right">

        {/* Switch user dropdown */}
        <div className="sa-topbar-switcher" ref={dropdownRef}>
          <button
            className="sa-topbar-switch-btn"
            onClick={() => setDropdownOpen((v) => !v)}
          >
            <span className="material-icons-sharp">swap_horiz</span>
            Switch user
            <span className="material-icons-sharp sa-topbar-chevron">
              {dropdownOpen ? 'expand_less' : 'expand_more'}
            </span>
          </button>

          {dropdownOpen && (
            <div className="sa-topbar-dropdown">
              <div className="sa-topbar-dropdown-header">
                <span className="material-icons-sharp">group</span>
                Users in <strong>{orgLabel}</strong>
              </div>

              {loading ? (
                <div className="sa-topbar-dropdown-loading">
                  <span className="material-icons-sharp rotating">sync</span>
                  Loading…
                </div>
              ) : users.length === 0 ? (
                <div className="sa-topbar-dropdown-empty">No users found.</div>
              ) : (
                <div className="sa-topbar-dropdown-list">
                  {users.map((u) => {
                    const isCurrent = u.uid === impersonation?.uid;
                    return (
                      <button
                        key={u.uid}
                        className={`sa-topbar-dropdown-item ${isCurrent ? 'sa-topbar-dropdown-item--current' : ''}`}
                        onClick={() => !isCurrent && handleSwitchUser(u)}
                        disabled={isCurrent}
                      >
                        <div className="sa-topbar-dropdown-avatar">
                          {u.displayName?.[0]?.toUpperCase() || '?'}
                        </div>
                        <div className="sa-topbar-dropdown-info">
                          <span className="sa-topbar-dropdown-name">{u.displayName}</span>
                          <span className="sa-topbar-dropdown-email">{u.email || 'no-email'}</span>
                        </div>
                        {u.role && <span className="sa-topbar-dropdown-role">{u.role}</span>}
                        {isCurrent && <span className="material-icons-sharp sa-topbar-dropdown-check">check</span>}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Exit */}
        <button className="sa-topbar-exit" onClick={handleExitAndReturn}>
          <span className="material-icons-sharp">exit_to_app</span>
          Exit
        </button>
      </div>
    </div>
  );
};

export default SuperAdminTopBar;